// Registers app/document event listeners with automatic cleanup

"use strict";

import { onMounted, onUnmounted } from "vue";
import { AppEvents, type AppEventsMap } from "@/control/app-events";

/**
 * Registers a global app event listener.
 * @param eventName App event name
 * @param handler Callback invoked when the event is emitted
 */
export function listenOnAppEvent<K extends keyof AppEventsMap>(eventName: K, handler: AppEventsMap[K]): void {
    AppEvents.AddEventListener(eventName, handler);
}

/**
 * Removes a previously registered global app event listener.
 * @param eventName App event name
 * @param handler Callback to unregister
 */
export function removeAppEventListener<K extends keyof AppEventsMap>(eventName: K, handler: AppEventsMap[K]): void {
    AppEvents.RemoveEventListener(eventName, handler);
}

/**
 * Registers a document-level event listener.
 * @param eventName DOM event name
 * @param listener DOM event handler
 */
export function listenOnDocumentEvent<K extends keyof DocumentEventMap>(
    eventName: K,
    listener: (this: Document, ev: DocumentEventMap[K]) => any,
): void {
    document.addEventListener(eventName, listener);
}

/**
 * Removes a previously registered document-level event listener.
 * @param eventName DOM event name
 * @param listener DOM event handler to unregister
 */
export function removeDocumentEventListener<K extends keyof DocumentEventMap>(
    eventName: K,
    listener: (this: Document, ev: DocumentEventMap[K]) => any,
): void {
    document.removeEventListener(eventName, listener);
}

/**
 * Registers an app event listener on mount and removes it on unmount.
 * @param eventName App event name
 * @param handler Callback invoked when the event is emitted
 */
export function useAppEventListener<K extends keyof AppEventsMap>(eventName: K, handler: AppEventsMap[K]): void {
    onMounted(() => {
        listenOnAppEvent(eventName, handler);
    });

    onUnmounted(() => {
        removeAppEventListener(eventName, handler);
    });
}

/**
 * Registers a document event listener on mount and removes it on unmount.
 * @param eventName DOM event name
 * @param listener DOM event handler
 */
export function useDocumentEventListener<K extends keyof DocumentEventMap>(
    eventName: K,
    listener: (this: Document, ev: DocumentEventMap[K]) => any,
): void {
    onMounted(() => {
        listenOnDocumentEvent(eventName, listener);
    });

    onUnmounted(() => {
        removeDocumentEventListener(eventName, listener);
    });
}
